<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { AppRoute } from '@/libs/routing';
import { useAlertsStore } from '@/stores/alerts';
import { useAppStore } from '@/stores/app';
import BaseIcon from '@/components/ui/BaseIcon.vue';

const appStore = useAppStore();
const alertStore = useAlertsStore();
const router = useRouter();

// The same groupId that decides whether App.vue shows the navbar: taking it
// from the group store alone leaves the links without one while the group is
// (re)loading, and resolving them then throws `Missing required param "groupId"`
// on routes that carry no groupId to fall back on.
defineProps<{ groupId: string }>();

// The Home tab is the map, and nothing else: the views stacked over it are
// reached from the Schedules tab, which stays lit while they are open.
const HOME_ROUTES = [AppRoute.MAP];

// The Schedules tab stays active on the standalone deep-link lists, the route /
// stop detail views, and every view stacked over them: trip, timetable, SNCF
// departure board and itinerary.
const SCHEDULES_ROUTES = [
  AppRoute.SCHEDULES,
  AppRoute.ROUTE_LIST,
  AppRoute.STOP_LIST,
  AppRoute.ROUTE_DETAILED,
  AppRoute.ON_DEMAND_ROUTE_DETAILED,
  AppRoute.STOP_DETAILED,
  AppRoute.TRIP_DETAILED,
  AppRoute.TIMETABLE,
  AppRoute.TRAIN_DEPARTURES,
  AppRoute.ITINERARY,
];

// The Infos tab stays active on the per-line traffic-info view.
const INFOS_ROUTES = [AppRoute.PASSENGER_MESSAGE, AppRoute.ROUTE_ALERTS];

const alertCountLabel = computed<string>(() => {
  return alertStore.alertCount > 9 ? '9+' : String(alertStore.alertCount);
});
</script>

<template>
  <nav class="navbar" :class="{ 'navbar--hidden': appStore.shouldHideNavbar }">
    <ul class="navbar__list">
      <li class="link">
        <router-link
          :to="{ name: AppRoute.MAP, params: { groupId: groupId } }"
          :class="{ 'link--active': HOME_ROUTES.includes(router.currentRoute.value.name as string) }"
        >
          <i class="link__icon"><BaseIcon name="compass" /></i>
          <span class="link__text">{{ $t('nav.home') }}</span>
        </router-link>
      </li>
      <li class="link">
        <router-link
          :to="{ name: AppRoute.SCHEDULES, params: { groupId: groupId } }"
          :class="{ 'link--active': SCHEDULES_ROUTES.includes(router.currentRoute.value.name as string) }"
        >
          <i class="link__icon"><BaseIcon name="clock" /></i>
          <span class="link__text">{{ $t('nav.schedules') }}</span>
        </router-link>
      </li>
      <li class="link">
        <router-link
          :to="{ name: AppRoute.PASSENGER_MESSAGE, params: { groupId: groupId } }"
          :class="{ 'link--active': INFOS_ROUTES.includes(router.currentRoute.value.name as string) }"
        >
          <i class="link__icon">
            <span v-if="alertStore.alertCount > 0" class="alerts-count">
              {{ alertCountLabel }}
            </span>
            <BaseIcon name="message-circle-warning" />
          </i>
          <span class="link__text">{{ $t('nav.infos') }}</span>
        </router-link>
      </li>
    </ul>
  </nav>
</template>

<style lang="scss" scoped>
.navbar {
  background-color: $color-white;
  bottom: 0;
  border-top: 1px solid $color-grey-100;

  height: $navbar-height;
  left: 0;
  position: fixed;
  right: 0;
  z-index: 2;
  &--hidden {
    display: none;
  }

  &__list {
    display: flex;
    justify-content: space-around;
    margin: 0 3%;

    .link {
      color: $color-neutral-highest;
      flex-grow: 1;
      max-width: 70px;
      padding: 8px 0;
      text-align: center;
      text-decoration: none;
      width: 70px;

      &--active {
        color: $color-primary;

        .link__text {
          font-weight: 600;
        }
      }

      &__icon {
        // Flex, not block: the wrapper now holds an inline SVG instead of an
        // icon-font ligature, and a line box would add the font strut's
        // descender on top of the glyph — 13px that push the labels past the
        // $navbar-height the navbar is locked to.
        align-items: center;
        display: flex;
        font-size: 25px;
        justify-content: center;
        margin: auto;
        position: relative;
        width: min-content;

        .alerts-count {
          background-color: $color-danger;
          border-radius: 8px;
          color: $color-white;
          font-family: $font-poppins;
          font-size: 0.5rem;
          height: 16px;
          line-height: 16px;
          min-width: 16px;
          padding: 0 2px;
          box-sizing: border-box;
          position: absolute;
          right: -5px;
          text-align: center;
          top: -5px;
          font-style: normal;
        }
      }

      &__text {
        display: block;
        font-size: 12px;
        margin-top: 0.3rem;
      }
    }
  }
}
</style>
